import BaseController from "./BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import Fragment from "sap/ui/core/Fragment";
import type Table from "sap/m/Table";
import type ColumnListItem from "sap/m/ColumnListItem";
import type Input from "sap/m/Input";
import type Dialog from "sap/m/Dialog";
import type List from "sap/m/List";
import type StepInput from "sap/m/StepInput";
import type { Button$PressEvent } from "sap/m/Button";
import type { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import type { ResponseError } from "../types/Errors";
import { problemMessage } from "../util/ProblemDetails";

interface DeploymentHeader {
	Name: string;
	OwnerOrgId: string;
	OwnerOrgName: string | null;
	Status: string;
}

interface ViewState {
	deploymentId: string;
	deploymentName: string;
	deploymentStatus: string;
	customerOrgId: string;
	customerName: string;
}

/** A row of /api/v1/deployments/{id}/bindable-entitlements, as far as the unbind dialog reads it. */
interface BindableRow {
	entitlementId: string;
	productName: string;
	productCode: string;
	licensingMode: string;
	boundHere: number;
}

/** An assigned user of an InPortal pool; licenseId is the seat that is released with the user. */
interface UnbindCandidate {
	licenseId: string;
	userName: string;
	userEmail: string;
}

interface UnbindState {
	licenseId: string;
	entitlementId: string;
	productName: string;
	productCode: string;
	inPortal: boolean;
	boundHere: number;
	/** Upper bound of the quantity: boundHere for an InPortal pool, the targeted license's quantity for OutOfPortal. */
	maxQuantity: number;
	/** OutOfPortal: how many licenses of the entitlement are bound here; the call targets one of them. */
	licenseCount: number;
	oneOfSeveral: boolean;
	quantity: number;
	emptySeats: number;
	candidates: UnbindCandidate[];
	selectedLicenseIds: string[];
	selectedCount: number;
	requiredSelections: number;
	needsUserSelection: boolean;
	canConfirm: boolean;
	busy: boolean;
}

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Strona przypisywania licencji on-prem do Deploymentu (route <c>deployment/{id}/bind-licenses</c>).
 * Per ADR-012: deployment nie ma już ProductFamily, więc pokazujemy WSZYSTKIE on-prem
 * entitlementy klienta (LicensingMode=OutOfPortal). User multi-select + qty.
 *
 * Każdy zaznaczony entitlement → POST <c>/api/v1/licenses</c> z <c>{ entitlementId, deploymentId, quantity }</c>.
 * Submit nieudany dla pojedynczego entitlement nie blokuje pozostałych — final raport agreguje sukcesy + błędy.
 *
 * @namespace computec.b2b.portal.controller
 */
export default class DeploymentBindLicenses extends BaseController {
	private unbindDialog?: Dialog;

	public override onInit(): void {
		const initial: ViewState = {
			deploymentId: "",
			deploymentName: "",
			deploymentStatus: "",
			customerOrgId: "",
			customerName: ""
		};
		this.getView().setModel(new JSONModel(initial), "view");
		// Mapa istniejących Licenses (entitlementId → {licenseId, quantity}) dla bieżącego deploymentu.
		// Używana do (a) pre-fill input z aktualną Quantity, (b) submit branchowanie PATCH vs POST.
		this.getView().setModel(new JSONModel({ byEntitlementId: {} as Record<string, { LicenseId: string; Quantity: number }> }), "existingLicenses");
		// Bindable entitlements (Purchased/ConsumedTotal/BoundHere/Available) from the dedicated endpoint.
		this.getView().setModel(new JSONModel({ entitlements: [] }), "bind");
		this.getView().setModel(new JSONModel(DeploymentBindLicenses.emptyUnbindState()), "unbind");
		this.getRouter().getRoute("deploymentBindLicenses")?.attachPatternMatched((e) => this.onPatternMatched(e));
	}

	private onPatternMatched(event: Route$PatternMatchedEvent): void {
		const args = event.getParameter("arguments") as { id?: string } | undefined;
		const id = args?.id ?? "";
		const view = this.getView().getModel("view") as JSONModel;
		view.setProperty("/deploymentId", id);
		view.setProperty("/deploymentName", "");
		view.setProperty("/deploymentStatus", "");
		view.setProperty("/customerOrgId", "");
		view.setProperty("/customerName", "");
		if (!UUID_RE.test(id)) {
			return;
		}
		void this.loadDeploymentAndCustomer(id);
	}

	/**
	 * Ładuje nagłówek deploymentu (Name, OwnerOrgId, Status) + nazwę klienta. Po tym aplikuje
	 * filtr na tabelę entitlementów (po OwnerOrgId i LicensingMode=OutOfPortal).
	 */
	private async loadDeploymentAndCustomer(deploymentId: string): Promise<void> {
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const dep = await fetcher.json<DeploymentHeader>(
				`/odata/v4/Deployments(${deploymentId})?$select=Name,OwnerOrgId,OwnerOrgName,Status`
			);
			const view = this.getView().getModel("view") as JSONModel;
			view.setProperty("/deploymentName", dep.Name);
			view.setProperty("/deploymentStatus", dep.Status);
			view.setProperty("/customerOrgId", dep.OwnerOrgId);
			// The owner name travels on the deployment (OwnerOrgName); no Organizations read, which a
			// caller without orgs.read would get 403 for.
			view.setProperty("/customerName", dep.OwnerOrgName ?? "");

			await this.loadBindable(deploymentId);
			await this.loadExistingLicenses(deploymentId);
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, "Failed to load deployment"));
		}
	}

	/**
	 * Loads the licenses that still occupy the deployment (Active, Suspended, EmptySeat) into the
	 * "existingLicenses" model. Submit uses it to choose between PATCH (existing binding) and POST
	 * (new binding); a Voided or Unbound license is not a binding, so binding again creates a new one.
	 */
	private async loadExistingLicenses(deploymentId: string): Promise<void> {
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const resp = await fetcher.json<{ value?: { Id: string; EntitlementId: string; Quantity: number }[] }>(
				`/odata/v4/Licenses?$filter=DeploymentId eq ${deploymentId} and Status in ('Active','Suspended','EmptySeat')&$select=Id,EntitlementId,Quantity`
			);
			// Agregujemy per entitlement — Σ Quantity = total seatów w binding (InPortal: N×1, OutOfPortal: 1×N).
			// LicenseId pierwszego seata wystarczy do PATCH-a (handler resolwuje pool przez entitlement+deployment).
			const byEntitlementId: Record<string, { LicenseId: string; Quantity: number }> = {};
			for (const l of resp.value ?? []) {
				const existing = byEntitlementId[l.EntitlementId];
				if (existing) {
					existing.Quantity += l.Quantity;
				} else {
					byEntitlementId[l.EntitlementId] = { LicenseId: l.Id, Quantity: l.Quantity };
				}
			}
			(this.getView().getModel("existingLicenses") as JSONModel).setProperty("/byEntitlementId", byEntitlementId);
		} catch {
			// Best-effort — brak istniejących = normal POST flow z domyślną quantity=1.
		}
	}

	/**
	 * Ładuje bindable entitlementy dla deploymentu z dedykowanego endpointu: per pula Purchased /
	 * ConsumedTotal (cała zajęta pula: Active+Suspended+EmptySeat, wszystkie deploymenty) / BoundHere /
	 * Available. Endpoint filtruje już pule wyczerpane na innych deploymentach.
	 */
	private async loadBindable(deploymentId: string): Promise<void> {
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const rows = await fetcher.json<unknown[]>(`/api/v1/deployments/${deploymentId}/bindable-entitlements`);
			(this.getView().getModel("bind") as JSONModel).setProperty("/entitlements", Array.isArray(rows) ? rows : []);
		} catch (e) {
			const err = e as ResponseError;
			(this.getView().getModel("bind") as JSONModel).setProperty("/entitlements", []);
			MessageBox.error(problemMessage(err.bodyText, "Failed to load entitlements"));
		}
	}

	public formatSubtitle(template: string | undefined, customer: string | undefined, deployment: string | undefined): string {
		if (!template) return "";
		return template.replace("{0}", customer ?? "—").replace("{1}", deployment ?? "—");
	}

	public override onNavBack(): void {
		const id = (this.getView().getModel("view") as JSONModel).getProperty("/deploymentId") as string;
		if (UUID_RE.test(id)) {
			this.navTo("deploymentDetail", { id });
		} else {
			this.navTo("deployments");
		}
	}

	public onNavToDeployments(): void {
		this.navTo("deployments");
	}

	// Quantity zmienione — placeholder hook, walidacja przy submit (≥ 1).
	public onQuantityChange(): void {
		// no-op; submit waliduje wszystkie row'y.
	}

	public async onSubmit(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const view = this.getView().getModel("view") as JSONModel;
		const deploymentId = view.getProperty("/deploymentId") as string;
		if (!UUID_RE.test(deploymentId)) {
			return;
		}

		const table = this.byId("entitlementsTable") as Table | undefined;
		const selected = (table?.getSelectedItems() ?? []) as ColumnListItem[];
		if (selected.length === 0) {
			MessageBox.warning(bundle.getText("deployments.bind.error.noSelection") ?? "Select at least one");
			return;
		}

		// Zbieramy { entitlementId, quantity } per zaznaczony rząd.
		// cells layout: [0 VBox product, 1 Family, 2 Purchased, 3 Assigned, 4 Here, 5 Available, 6 Quantity Input, 7 ValidUntil, 8 Unbind]
		const requests: { entitlementId: string; quantity: number; productName: string }[] = [];
		// Rows refused before any call: lowering a binding goes through Unbind, never through PATCH.
		const rejected: string[] = [];
		const useUnbindText = bundle.getText("deployments.unbind.useUnbindToDecrease") ?? "Use Unbind to lower the quantity";
		for (const item of selected) {
			const ctx = item.getBindingContext("bind");
			const entitlementId = ctx?.getProperty("entitlementId") as string | undefined;
			const productName = (ctx?.getProperty("productName") as string | undefined) ?? "";
			const boundHere = Number(ctx?.getProperty("boundHere") ?? 0);
			if (!entitlementId || !UUID_RE.test(entitlementId)) {
				continue;
			}
			const cells = item.getCells();
			const qtyInput = cells[6] as Input | undefined;
			const qty = Number(qtyInput?.getValue() ?? "0");
			if (!Number.isFinite(qty)) {
				continue;
			}
			if (boundHere > 0 && qty < boundHere) {
				rejected.push(`${productName}: ${useUnbindText}`);
				continue;
			}
			if (qty < 1) {
				continue;
			}
			requests.push({ entitlementId, quantity: qty, productName });
		}
		if (requests.length === 0 && rejected.length > 0) {
			MessageBox.warning(rejected.join("\n"));
			return;
		}
		if (requests.length === 0) {
			MessageBox.warning(bundle.getText("deployments.bind.error.noSelection") ?? "Select at least one");
			return;
		}

		const fetcher = this.getOwnerComponent().getAuthFetch();
		const existing = (this.getView().getModel("existingLicenses") as JSONModel)
			.getProperty("/byEntitlementId") as Record<string, { LicenseId: string; Quantity: number }> | undefined;
		const errors: string[] = [...rejected];
		const attempted = requests.length + rejected.length;
		let succeeded = 0;
		for (const req of requests) {
			try {
				const existingLicense = existing?.[req.entitlementId];
				if (existingLicense) {
					// PATCH istniejącego bindingu — orders-and-entitlements.md:88. Idempotentne gdy
					// quantity bez zmian (handler skip'uje SaveChanges przez License.ChangeQuantity).
					await fetcher.json(`/api/v1/licenses/${existingLicense.LicenseId}/quantity`, {
						method: "PATCH",
						body: JSON.stringify({ quantity: req.quantity })
					});
				} else {
					// Nowy binding — POST tworzy License (InPortal: N×Qty=1 EmptySeat; OutOfPortal: 1×Qty=N).
					await fetcher.json("/api/v1/licenses", {
						method: "POST",
						body: JSON.stringify({
							entitlementId: req.entitlementId,
							deploymentId,
							quantity: req.quantity
						})
					});
				}
				succeeded += 1;
			} catch (e) {
				const err = e as ResponseError;
				const msg = problemMessage(err.bodyText, "");
				errors.push(`${req.productName}: ${msg || "failed"}`);
			}
		}

		if (errors.length === 0) {
			const text = bundle.getText("deployments.bind.success", [succeeded.toString()])
				?? `${succeeded} license(s) bound`;
			MessageToast.show(text);
			this.getOwnerComponent().getModel("odata")?.refresh();
			this.navTo("deploymentDetail", { id: deploymentId });
		} else if (succeeded > 0) {
			const header = bundle.getText("deployments.bind.partial", [succeeded.toString(), attempted.toString()])
				?? `Bound ${succeeded}/${attempted}`;
			MessageBox.warning(`${header}\n\n${errors.join("\n")}`);
		} else {
			const header = bundle.getText("deployments.bind.failure") ?? "Failed to bind licenses";
			MessageBox.error(`${header}\n\n${errors.join("\n")}`);
		}
	}

	// === Unbind (card 61) ===

	public async onOpenUnbindDialog(event: Button$PressEvent): Promise<void> {
		const row = event.getSource().getBindingContext("bind")?.getObject() as BindableRow | undefined;
		const deploymentId = (this.getView().getModel("view") as JSONModel).getProperty("/deploymentId") as string;
		if (!row || !UUID_RE.test(deploymentId) || !UUID_RE.test(row.entitlementId) || row.boundHere < 1) {
			return;
		}

		const inPortal = row.licensingMode === "InPortal";
		const model = this.getUnbindModel();
		model.setData({
			...DeploymentBindLicenses.emptyUnbindState(),
			entitlementId: row.entitlementId,
			productName: row.productName,
			productCode: row.productCode,
			inPortal,
			boundHere: row.boundHere,
			maxQuantity: row.boundHere,
			quantity: row.boundHere,
			busy: true
		});

		const dialog = await this.getUnbindDialog();
		(this.byId("unbindAssignmentsList") as List | undefined)?.removeSelections(true);
		dialog.open();

		try {
			await this.loadUnbindPool(deploymentId, row.entitlementId, inPortal);
		} catch (e) {
			const bundle = await this.getResourceBundle();
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("deployments.unbind.failure") ?? "Failed to unbind the license"));
		} finally {
			model.setProperty("/busy", false);
			this.refreshUnbindState();
		}
	}

	public onUnbindQuantityChange(): void {
		const value = (this.byId("unbindQuantity") as StepInput | undefined)?.getValue();
		this.getUnbindModel().setProperty("/quantity", Number(value ?? 0));
		this.refreshUnbindState();
	}

	public onUnbindSelectionChange(): void {
		const list = this.byId("unbindAssignmentsList") as List | undefined;
		const ids = (list?.getSelectedContexts() ?? [])
			.map((ctx) => (ctx.getObject() as UnbindCandidate).licenseId);
		this.getUnbindModel().setProperty("/selectedLicenseIds", ids);
		this.refreshUnbindState();
	}

	public onCancelUnbind(): void {
		this.unbindDialog?.close();
	}

	public async onConfirmUnbind(): Promise<void> {
		this.refreshUnbindState();
		const model = this.getUnbindModel();
		const state = model.getData() as UnbindState;
		// canConfirm includes !busy; busy is set before the first await so a second click returns here.
		if (!state.canConfirm) {
			return;
		}
		model.setProperty("/busy", true);
		this.refreshUnbindState();

		const deploymentId = (this.getView().getModel("view") as JSONModel).getProperty("/deploymentId") as string;
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const bundle = await this.getResourceBundle();
			try {
				await fetcher.json(`/api/v1/licenses/${state.licenseId}/unbind`, {
					method: "POST",
					body: JSON.stringify({
						quantity: state.quantity,
						releaseAssignedSeatIds: state.needsUserSelection ? state.selectedLicenseIds : []
					})
				});
			} catch (e) {
				const err = e as ResponseError;
				MessageBox.error(problemMessage(err.bodyText, bundle.getText("deployments.unbind.failure") ?? "Failed to unbind the license"));
				return;
			}

			this.unbindDialog?.close();
			MessageToast.show(bundle.getText("deployments.unbind.success", [state.quantity.toString(), state.productName])
				?? `${state.quantity} license(s) unbound`);
			this.getOwnerComponent().getModel("odata")?.refresh();
			await this.loadBindable(deploymentId);
			await this.loadExistingLicenses(deploymentId);
		} finally {
			model.setProperty("/busy", false);
			this.refreshUnbindState();
		}
	}

	private async getUnbindDialog(): Promise<Dialog> {
		if (!this.unbindDialog) {
			this.unbindDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.UnbindLicenseDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.unbindDialog);
		}
		return this.unbindDialog;
	}

	/**
	 * Reads the licenses of the entitlement still bound to this deployment and picks the target of the call.
	 * For an InPortal pool any seat will do (the server resolves the pool), and since empty seats go first,
	 * only the part of the quantity above them needs named users - so the assigned users are read as well.
	 * OutOfPortal may hold several licenses of one entitlement here; the call unbinds from one of them, so the
	 * dialog targets the largest (ties by Id) and caps the quantity at it. The row keeps Unbind for the rest.
	 */
	private async loadUnbindPool(deploymentId: string, entitlementId: string, inPortal: boolean): Promise<void> {
		const fetcher = this.getOwnerComponent().getAuthFetch();
		const seats = await fetcher.json<{ value?: { Id: string; Status: string; Quantity: number }[] }>(
			`/odata/v4/Licenses?$filter=DeploymentId eq ${deploymentId} and EntitlementId eq ${entitlementId} and Status in ('Active','Suspended','EmptySeat')&$select=Id,Status,Quantity`
		);
		const seatRows = seats.value ?? [];
		const model = this.getUnbindModel();
		if (!inPortal) {
			const target = [...seatRows].sort((a, b) => b.Quantity - a.Quantity || a.Id.localeCompare(b.Id))[0];
			model.setProperty("/licenseId", target?.Id ?? "");
			if (target) {
				model.setProperty("/maxQuantity", target.Quantity);
				model.setProperty("/quantity", target.Quantity);
			}
			model.setProperty("/licenseCount", seatRows.length);
			model.setProperty("/oneOfSeveral", seatRows.length > 1);
			return;
		}
		model.setProperty("/licenseId", seatRows[0]?.Id ?? "");
		const assignedSeatIds = seatRows.filter((seat) => seat.Status !== "EmptySeat").map((seat) => seat.Id);
		model.setProperty("/emptySeats", seatRows.length - assignedSeatIds.length);
		model.setProperty("/candidates", await this.loadUnbindCandidates(assignedSeatIds));
	}

	/** Same query the InPortal pools of DeploymentDetail use (enrichWithAssignmentInfo). */
	private async loadUnbindCandidates(seatIds: string[]): Promise<UnbindCandidate[]> {
		if (seatIds.length === 0) {
			return [];
		}
		const fetcher = this.getOwnerComponent().getAuthFetch();
		const seatFilter = seatIds.map((id) => `LicenseId eq ${id}`).join(" or ");
		const data = await fetcher.json<{ value?: {
			Id: string;
			LicenseId: string;
			AssignedUserId: string;
			UserEmail: string;
			UserFirstName: string | null;
			UserLastName: string | null;
			AssignedAt: string;
		}[] }>(
			`/odata/v4/LicenseAssignments?$filter=Status%20eq%20'Active'%20and%20(${encodeURIComponent(seatFilter)})&$select=Id,LicenseId,AssignedUserId,UserEmail,UserFirstName,UserLastName,AssignedAt`
		);
		return (data.value ?? [])
			.map((a): UnbindCandidate => ({
				licenseId: a.LicenseId,
				userName: `${a.UserFirstName ?? ""} ${a.UserLastName ?? ""}`.trim() || a.UserEmail,
				userEmail: a.UserEmail
			}))
			.sort((a, b) => a.userName.localeCompare(b.userName));
	}

	/**
	 * Derives what the dialog shows from quantity, empty seats and selection: the user list appears once
	 * the quantity exceeds the empty seats, and confirm needs exactly that many users selected.
	 */
	private refreshUnbindState(): void {
		const model = this.getUnbindModel();
		const state = model.getData() as UnbindState;
		const quantity = Number(state.quantity);
		const validQuantity = Number.isInteger(quantity) && quantity >= 1 && quantity <= state.maxQuantity;
		const requiredSelections = state.inPortal ? Math.max(0, quantity - state.emptySeats) : 0;
		const needsUserSelection = state.inPortal && requiredSelections > 0;
		model.setProperty("/selectedCount", state.selectedLicenseIds.length);
		model.setProperty("/requiredSelections", requiredSelections);
		model.setProperty("/needsUserSelection", needsUserSelection);
		model.setProperty("/canConfirm", !state.busy
			&& validQuantity
			&& UUID_RE.test(state.licenseId)
			&& (!needsUserSelection || state.selectedLicenseIds.length === requiredSelections));
	}

	private getUnbindModel(): JSONModel {
		return this.getView().getModel("unbind") as JSONModel;
	}

	private static emptyUnbindState(): UnbindState {
		return {
			licenseId: "",
			entitlementId: "",
			productName: "",
			productCode: "",
			inPortal: false,
			boundHere: 0,
			maxQuantity: 0,
			licenseCount: 0,
			oneOfSeveral: false,
			quantity: 0,
			emptySeats: 0,
			candidates: [],
			selectedLicenseIds: [],
			selectedCount: 0,
			requiredSelections: 0,
			needsUserSelection: false,
			canConfirm: false,
			busy: false
		};
	}

}
